import React, { useState } from 'react';
import { Button, message, Tooltip, Upload } from 'antd';
import type { UploadFile } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined, EyeOutlined, UploadOutlined } from '@ant-design/icons';
import { API_ROOT, authenticatedUploadProps } from '@partsbadger/utils';
import QuoteStore from '../../stores/QuoteStore';
import { UploadProps } from 'antd/es/upload';
import { QuoteLineItemProps } from '@partsbadger/types';
import { UploadChangeParam } from 'antd/lib/upload';
import { EmbeddedAttachment } from '@partsbadger/library';
import { getUploadButtonLabel, getUploadLabelClass, isUploadInProgress, UploadStage } from '../../utils/uploadProgress';

interface Props extends UploadProps {
    item: QuoteLineItemProps;
    readOnly?: boolean;
    /**
     * Skip the built-in "2D Drawing File:" label and the block-width upload
     * button — used when the caller renders its own label (e.g. a fixed
     * "2D File:" column) and needs a compact, consistently-sized control
     * regardless of upload state.
     */
    compact?: boolean;
    onUploadStateChange?: (uploading: boolean) => void;
}

export const UploadDrawing = ({ item, readOnly = false, compact = false, onUploadStateChange }: Props) => {
    const { files_2d } = item.product?.attachments || {};

    const analysis2dStatus = item.product.analysis_status?.file_2d;
    const has2dError = analysis2dStatus?.status === 'FAILURE';
    const errorMsg = analysis2dStatus?.message;

    const defaultFileList: UploadFile[] = files_2d?.id
        ? [
              {
                  uid: String(files_2d.id),
                  name: files_2d.filename,
                  status: 'done',
                  response: {
                      id: files_2d.id,
                      file: files_2d.file,
                      filename: files_2d.filename,
                  },
              },
          ]
        : [];

    const [fileList, setFileList] = useState(defaultFileList);
    const [uploadStage, setUploadStage] = useState<UploadStage>('idle');
    const [pendingFilename, setPendingFilename] = useState('');
    const [uploadPercent, setUploadPercent] = useState(0);
    const uploadInProgress = isUploadInProgress(uploadStage);

    const uploadUrl = `${API_ROOT}/customer/drawing-files/`;
    const uploadAuthProps = QuoteStore.token ? authenticatedUploadProps(uploadUrl) : { action: uploadUrl };

    const attachDrawingToLineItem = async (drawingFileId: number) => {
        const saved = await QuoteStore.updateLineItemFiles({
            id: item.id,
            product: { file_2d: drawingFileId },
        });
        if (!saved) {
            throw new Error('The drawing was not attached');
        }
    };

    const handleUploadDone = async (info: UploadChangeParam) => {
        const drawingFileId = info.file.response?.id;
        const attachments: EmbeddedAttachment[] = info.file.response?.embedded_attachments || [];

        if (!drawingFileId) {
            message.error('The 2D drawing uploaded, but it could not be attached. Please try again.');
            setFileList([]);
            setUploadStage('idle');
            onUploadStateChange?.(false);
            return;
        }

        setUploadStage('saving');

        try {
            await attachDrawingToLineItem(drawingFileId);
            QuoteStore.queueEmbeddedExtractionFromUpload(item.id, drawingFileId, attachments, info.file.name);
        } catch {
            setFileList([]);
            message.error('The 2D drawing could not be saved to this part. Please try again.');
        } finally {
            setUploadStage('idle');
            setUploadPercent(0);
            onUploadStateChange?.(false);
        }
    };

    const props: UploadProps = {
        name: 'file',
        multiple: false,
        accept: '.pdf, .dxf',
        ...uploadAuthProps,
        defaultFileList: defaultFileList,
        fileList: fileList,
        onChange: (info: UploadChangeParam) => {
            const { status } = info.file;

            const nextFileList = info.fileList.slice(-1);
            setFileList(nextFileList);

            if (status === 'uploading') {
                onUploadStateChange?.(true);
                setPendingFilename(info.file.name);
                setUploadPercent(Math.round(info.file.percent || 0));
                setUploadStage('uploading');
            }

            if (status === 'done') {
                void handleUploadDone(info);
            }

            if (status === 'error') {
                onUploadStateChange?.(false);
                setUploadStage('idle');
                setUploadPercent(0);
                message.error(`${info.file.name} could not be uploaded. Please try again.`);
            }

            if (status === 'removed') {
                onUploadStateChange?.(false);
                setUploadStage('idle');
                setUploadPercent(0);
                void QuoteStore.updateLineItemFiles({
                    id: item.id,
                    product: { file_2d: null },
                });
            }
        },
    };

    if (!item?.uid) {
        return null;
    }

    const AlertIcon = has2dError ? (
        <Tooltip title={errorMsg}>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginLeft: '8px' }} />
        </Tooltip>
    ) : null;

    const open2dPdfInPopup = (e: React.MouseEvent) => {
        e.stopPropagation();
        const url = files_2d?.file;
        if (!url) return;
        const features = [
            'popup=yes',
            'width=960',
            'height=800',
            'left=120',
            'top=80',
            'scrollbars=yes',
            'resizable=yes',
            'menubar=no',
            'toolbar=no',
        ].join(',');
        const win = window.open(url, 'pb-2d-drawing-pdf', features);
        win?.focus();
    };

    const remove2dFile = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setFileList([]);
        const saved = await QuoteStore.updateLineItemFiles({
            id: item.id,
            product: { file_2d: null },
        });
        if (!saved) {
            // The drawing is still attached on the server: show it again.
            setFileList(defaultFileList);
        }
    };

    return (
        <>
            <Upload
                // A bottom margin belongs to the legacy vertical form layout,
                // but shifts the inline 2D controls upward in the Part Header.
                className={compact ? undefined : 'mb-2'}
                {...props}
                // The compact Part Header renders its own filename and remove
                // action, so the Upload list would duplicate the filename.
                showUploadList={
                    compact
                        ? false
                        : {
                              showRemoveIcon: !readOnly,
                          }
                }
            >
                {!files_2d && !readOnly ? (
                    <Button
                        className={compact ? 'ant-btn-xs p-0' : 'ant-btn-xs'}
                        block={!compact}
                        disabled={uploadInProgress}
                        loading={uploadInProgress}
                        size={compact ? 'small' : undefined}
                        type={compact ? 'link' : 'default'}
                        icon={uploadInProgress ? undefined : <UploadOutlined />}
                        style={compact ? { display: 'inline-flex', alignItems: 'center' } : undefined}
                    >
                        <span aria-live="polite" className={getUploadLabelClass(uploadStage)}>
                            {getUploadButtonLabel(
                                uploadStage,
                                pendingFilename,
                                uploadPercent,
                                compact ? 'Upload' : 'Add 2D Drawing File'
                            )}
                        </span>
                    </Button>
                ) : (
                    <div onClick={e => e.stopPropagation()}>
                        {!compact ? <span className="font-weight-bold">2D Drawing File:</span> : null}
                        {files_2d?.file ? (
                            <>
                                <Button
                                    type="link"
                                    size="small"
                                    className={compact ? 'p-0' : 'p-0 ml-2'}
                                    icon={<EyeOutlined />}
                                    onClick={open2dPdfInPopup}
                                    style={compact ? { display: 'inline-flex', alignItems: 'center' } : undefined}
                                >
                                    {files_2d.filename}
                                </Button>
                                {!readOnly ? (
                                    <Tooltip title="Remove 2D file">
                                        <Button
                                            type="link"
                                            danger
                                            size="small"
                                            className="p-0 ml-1"
                                            icon={<DeleteOutlined />}
                                            aria-label="Remove 2D file"
                                            onClick={remove2dFile}
                                            style={{ display: 'inline-flex', alignItems: 'center' }}
                                        />
                                    </Tooltip>
                                ) : null}
                            </>
                        ) : null}
                        {AlertIcon}
                    </div>
                )}
            </Upload>
        </>
    );
};
